<template>
  <div class="select-number-list__wrap zone-bundle-list__wrap">
    <div class="select-number-list__wrap--item" v-for="(item, index) in currentSelectedOptions" :key="item.id">
      <div class="select-number-list__wrap--item__left">
        <span v-if="!showPriceEnable && isMobile" class="mobile-prefix">{{selectTitle}}</span>
        <zm-tooltip :disabled="!item.inputTooltip" placement="top">
          <template #content>
            <div v-html="item.inputTooltip"></div>
          </template>
          <zm-select data-testid="checkout-select-160" :ref='`select-${item.id}`' @change='onSelectionChange'
            v-model="item.id" size="lg" :filter-label="getFilterLabel"
            :aria-label="!showPriceEnable && isMobile ? `Select ${selectTitle} ${item.name}` : getSelectLabel(item)"
            :disabled="item.shouldDisabled ? item.shouldDisabled : item.disabled" popper-append-to-body
            class="select-number-list__wrap--item__select" popper-class="select-number-list-dropdown"
            :class="{'select-number-list__wrap--item__select--simple': hasNoDesc(getOptionList(item.id))}">
            <zm-option :data-testid="`checkout-option-161-${option.id}`" v-for="option in getOptionList(item.id)"
              :key="option.id" :value="option.id" class="select-number-list-dropdown__option" :aria-label="option.name"
              :label="option.name">
              <div class="select-number-list-dropdown__title">{{ option.name }}</div>
              <div class="select-number-list-dropdown__desc">{{ option.desc }}</div>
            </zm-option>
            <template #prefix>
              <span class="">{{ getFilterObj(item.id).name }}</span>
            </template>
          </zm-select>
        </zm-tooltip>
        <span v-if="!showPriceEnable && label && !isMobile" class="select-number-list__label">{{ label }}</span>
        <span v-if="showPriceEnable" class="select-number-list__label">{{ regionPriceMap[item.id] }}</span>
        <zm-tooltip v-if="getFilterObj(item.id).tooltip" placement="top">
          <template #content>
            <div v-html="getFilterObj(item.id).tooltip"></div>
          </template>
          <tips-icon type="info" data-testid="checkout-button-162" :aria-label="getLearnMoreLabel(item)" />
        </zm-tooltip>
      </div>
      <div class="select-number-list__wrap--item__right" :class="{ 'space_between': isMobile && showPriceEnable }">
        <span v-if="isMobile && !showPriceEnable" class="mobile-prefix">{{ inputTitle }}</span>

        <!-- Expanded state: render normally -->
        <component :is="getInputNumberComponent()" v-model="item.number" v-bind="getInputNumberProps(item)" :data-testid="`checkout-input-license-zp-${item.id}`" />

        <zm-tooltip v-if="deletable" :disabled="!item.deleteTips" placement="top">
          <template #content>
            <div v-html="item.deleteTips"></div>
          </template>
          <tips-icon
            v-if="deletable"
            data-testid="checkout-button-163"
            type="manualClose"
            class="select-number-list__wrap--item__close"
            :disabled="item.shouldDisabled ? item.shouldDisabled : (item.disabled || item.disableDelete)"
            :aria-label="getRemoveLabel(item)"
            @click="removeItem(item)"
            />
        </zm-tooltip>
      </div>
      <div class="select-number-list__wrap--item__divider" v-if="index < currentSelectedOptions.length - 1"></div>
    </div>
  </div>
</template>
<script lang="jsx">
/**
 * Select Number List Component
 *
 * @props {{ id: string, name: string, desc: string, tooltip: string, min: number, max: number, disabled: boolean, disableDelete: boolean }} optionList - All select option list
 * @props {{ id: string, number: number }} selectedOptions - Selected option list
 * @props { boolean } deletable - Whether you can delete item
 * @props { function } beforeChange - Before change item
 * @props { string } title - Title of select number list
 * @method newItem - Add new item
 */
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import { ManualCloseMark } from '@zoom/icons-vue';
import InputNumberWithPopoverUpsell from '@/components/input-number/input-number-with-popover-upsell';
import { Teleport } from 'vue';
import { planTypes } from '@/common/constants';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: 'SelectNumberList',
  components: {
    InputNumberWithPopover,
    InputNumberWithPopoverUpsell,
    Teleport
  },
  setup() {
    return {
      ManualCloseMark
    };
  },
  props: {
    // Add modelValue prop for Vue 3 v-model support
    modelValue: {
      type: Array,
      default: () => []
    },
    showPrice: {
      type: Boolean,
      required: false,
      default: false
    },
    bundlePlans: {
      type: Array,
      required: false,
      default: () => []
    },
    dataTracking: {
      type: Object,
      required: false,
    },
    optionList: {
      type: Array,
      default: () => [],
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    beforeChange: {
      type: Function,
      default: null,
    },
    title: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    selectTitle: {
      type: String,
      default: ''
    },
    inputTitle:{
      type: String,
      default: ''
    },
    // new add: target for teleport for select number
    numberTeleportTarget: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      currentSelectedOptions: [],
    };
  },
  computed: {
    // Direct computed property for selected options - no need for separate selectedOptionsValue
    selectedOptions() {
      return this.modelValue;
    },
    canSelectOptions() {
      return this.optionList.filter((item) => {
        return !this.currentSelectedOptions.some(option => option.id === item.id);
      });
    },
    watchCurrentSelectedOptions() {
      return JSON.parse(JSON.stringify(this.currentSelectedOptions));
    },
    isMobile(){
      return this.is_mobile_reactive || this.is_tablet_reactive
    },
    regionPriceMap() {
      const _priceMap = {};
      this.optionList.forEach(item => {
        const plan = this.bundlePlans.find(x => x.region === item.id);
        _priceMap[item.id] = null
        if (plan) {
          _priceMap[item.id] = this.$money(plan.amount, { forceDiff: true }) + this.$t(`billing.common.per_billing_cycle_user.${plan.billing_cycle}`)
        }
      })
      return _priceMap;
    },
    showPriceEnable() {
      return this.showPrice && !Object.values(this.regionPriceMap).includes(null);
    }
  },
  watch: {
    watchCurrentSelectedOptions: {
      handler(nv, ov) {
        if (this.beforeChange && !this.updating) {
          // Process data updates outside the component.
          this.beforeChange(nv, ov, (result) => {
            // Emit both Vue 3 v-model event and Vue 2 change event for backward compatibility
            this.$emit('update:modelValue', result.map((item) => (item)));
            this.$emit('change', result.map((item) => (item)));
            this.$nextTick(() => {
              this.initData();
            })
          });
        } else {
          // Emit both Vue 3 v-model event and Vue 2 change event for backward compatibility
          this.$emit('update:modelValue', nv.map((item) => (item)));
          this.$emit('change', nv.map((item) => (item)));
        }
      },
      deep: true,
    },
    selectedOptions: {
      handler(nv, ov) {
        if (JSON.stringify(nv) !== JSON.stringify(ov)) {
          this.initData();
        }
      },
      deep: true,
    },
  },
  methods: {
    initData() {
      this.updating = true;
      this.currentSelectedOptions = this.selectedOptions.map((item) => {
        const option = this.optionList.find(option => option.id === item.id);
        return {
          ...item,
          ...option,
        };
      });
      this.$nextTick(() => {
        this.updating = false;
      });
    },
    getOptionList(activeId) {
      const currentActiveOption = this.currentSelectedOptions.find(o => o.id === activeId);
      const otherSelectOptions = currentActiveOption?.disableSwitch? [] : this.canSelectOptions;
      return [this.optionList.find(option => option.id === activeId), ...otherSelectOptions].filter(option => Boolean(option));
    },
    hasNoDesc(options) {
      return options.every(x => !x.desc);
    },
    getFilterObj(keyValue) {
      if (this.optionList.length === 0) {
        return {id: '', name: '', desc: '', tooltip: ''}
      }
      const foundOption = this.optionList.find(option => option.id === keyValue);
      if (!foundOption) {
        return {id: '', name: '', desc: '', tooltip: ''}
      }
      return foundOption;
    },
    getFilterLabel(currentLabel, value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return this.getFilterObj(value).desc;
    },
    getSelectLabel(item) {
      return `${item.name} ${this.label}`;
    },
    getRemoveLabel(item) {
      const {name, desc} = this.getFilterObj(item.id);
      return `Remove ${this.title} ${name} ${desc}`
    },
    getLearnMoreLabel(item) {
      const {name} = this.getFilterObj(item.id);
      return `Learn more about ${name}`
    },
    removeItem(item) {
      this.currentSelectedOptions = this.currentSelectedOptions.filter(option => option.id !== item.id);
      if (this.dataTracking) {
        this.$tracking.buyInteraction(this.dataTracking?.buyInteraction || '',
            `${this.dataTracking?.buySelection || ''} ${item?.name || ''} - remove`);
      }
    },
    newItem() {
      let defaultNumber = 1;
      if (this.$context.is.upsell) {
        defaultNumber = this.canSelectOptions[0]._currentQuantity + 1;
      }
      this.canSelectOptions.length && this.currentSelectedOptions.push({
        ...this.canSelectOptions[0],
        number: defaultNumber,
      });
    },
    onSelectionChange(id) {
      if (this.dataTracking) {
        let name = this.optionList.find(item => item.id === id)?.name || '';
        this.$tracking.buyInteraction(this.dataTracking?.buyInteraction || '',
            `${this.dataTracking?.buySelection || ''} ${name} - select`);
      }
      this.$emit('optionSelected', this.optionList.find(item => item.id === id))
      /** *
       * ZOOM-679898
       * Since the zbo-select component changes with the change of the element ID,
       * the previous element cannot be focused,
       * so it is necessary to manually focus on the new zbo-select component.
       */
      this.$nextTick(() => {
        const targetRef = this.$refs[`select-${id}`];
        const refEle = targetRef?.[0]?.$el;
        if (refEle) {
          const innerEle = refEle.querySelector('.zbo-select-span__inner');
          innerEle && innerEle.focus();
        }
      });
    },
    // Calculate InputNumber label uniformly
    getInputLabel(item) {
      const baseLabel = `${item.name} ${item.desc} ${this.label}`;
      const suffix = this.isMobile && !this.showPriceEnable && this.inputTitle
        ? `Select ${this.inputTitle}`
        : 'Select the number of plans';
      return `${baseLabel} ${suffix}`;
    },
    // Get InputNumber component based on upsell state
    getInputNumberComponent() {
      return this.$context.is.upsell ? InputNumberWithPopoverUpsell : InputNumberWithPopover;
    },
    // Get InputNumber component props with unified logic
    getInputNumberProps(item) {
      const trackingName = getTrackingProductNameByPlanType(planTypes[item?.type])

      const dataTracking = {
        buyInteraction: 'number of licenses',
        planName: `${trackingName} - ${item?.name}`,
      }

      const commonProps = {
        dataTracking,
        size: 'lg',
        min: item.min,
        max: item.max,
        'controls-position': 'both',
        label: this.getInputLabel(item),
        disabled: item.disabled,
        teleportTarget: this.numberTeleportTarget,
        decreaseTips: item?.minTips,
      };

      // If in upsell mode, add oldNumber prop
      if (this.$context.is.upsell) {
        return {
          ...commonProps,
          oldNumber: item._currentQuantity || 0,
        };
      }

      return commonProps;
    },
  },
  created() {
    this.initData()
  },
}
</script>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

.zone-bundle-list__wrap {
  .select-number-list__wrap--item {
    align-items: flex-start;
  }
}

.opc-modal {
  .select-number-list__wrap--item__left{
    @include media(mobile,tablet){
      flex-direction: row;
    }
  }
}

.select-number-list {
  &__wrap {
    margin: 0;

    &--item {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;

      .zbo-input {
        // &.is-disabled {
        //   .zbo-input__inner {
        //     border-color: #b8b8c4;

        //     &:hover {
        //       border-color: #b8b8c4;
        //     }
        //   }
        // }
      }

      // & .zbo-select {
      //   border-color: #B8B8C4;
      // }

      &__divider {
        width: 100%;
        height: 1px;
        background-color: var(--zoom-color-border-subtle-neutral, #DFE3E8);
        margin: 12px 0;
      }

      .mobile-prefix{
        width: 100%;
      }

      &__left {
        flex-grow: 1;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        @include media(desktop){
          max-width: 400px; // tmp fix in case it gets too wide
        }
        @include media(mobile,tablet){
          justify-content: flex-start;
          flex-wrap: wrap;
          width: 100%;
          flex-direction: column;
          align-items: flex-start;
        }
        .select-number-list__wrap--item__select{
          flex-grow: 1;
          @include media(desktop){
            max-width: calc(100% - 32px - 12px); // tooltip width + safe margin
          }
        }
      }

      &__right {
        display: flex;
        align-items: center;
        @include media(mobile,tablet){
          margin-top: 10px;
          justify-content: flex-start;
          width: 100%;
          &.space_between {
            justify-content: space-between;
          }
          .zbo-input__inner{
            font-size: 14px;
          }
          .zbo-input-number--lg {
              width: 164px;
          }
        }
      }

      &__select {
        width: 265px;

        &--simple {
          width: 200px;

          .zbo-select-span__inner {
            min-width: 0px !important;
            font-size: 0 !important;
            visibility: visible !important;
          }
        }

        & .zbo-select-prefix {
          font-size: 18px;
          font-weight: 700;
          color: var(--zoom-color-text-primary, #0D6BDE);
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &.is-disabled .zbo-select-prefix {
          color: var(--zoom-color-text-neutral, #686F79);
        }

        & .zbo-select-span__inner {
          font-size: 14px;
          font-weight: 500;
          color: var(--zoom-color-text-strong-neutral, #555B62);
        }

        & .zbo-select__caret {
          color: var(--zoom-color-text-primary, #0D6BDE) !important;
          font-size: 16px !important;
        }

      }

      &__number {
        font-weight: 500;
        color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
        margin-left: 8px;
        @include media(mobile,tablet){
          margin-left: 10px !important;
        }
      }

      &__close {
        width: 20px;
        margin-left: 2px;
        color: var(--zoom-color-text-neutral, #686F79) !important;

        &:not(.is-disabled):hover {
          color: var(--zoom-color-state-subtle-error-hover, rgb(232 23 61 / 0.12)) !important;
          background: none !important;
        }
      }
    }
  }

  &__label {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    padding-left: 8px;
    padding-right: 8px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    white-space: nowrap;

    @include media(mobile,tablet){
      white-space: normal;
      word-break: break-all;
      overflow-wrap: break-word;
      line-height: 38px;
    }
  }
}

.zone-bundle-list__wrap {
  .select-number-list__label {
    line-height: 38px;
  }
}

.select-number-list-dropdown {
  .zbo-select-dropdown__item {
    &.selected {
      background-color: var(--zoom-color-state-subtle-primary-hover, rgb(14 114 237 / 0.12));
    }

    &:active {
      background-color: var(--zoom-color-state-subtle-primary-hover, rgb(14 114 237 / 0.12));
    }
  }

  .zbo-select-dropdown__item--content {
    display: flex;
    align-items: center;
    line-height: 24px;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: var(--zoom-color-text-primary, #0D6BDE);
    white-space: nowrap;
    margin-right: 4px;
  }

  &__desc {
    font-size: 14px;
    font-weight: 500;
    color: var(--zoom-color-text-strong-neutral, #555B62);
  }
}
</style>
